import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowLeft, Mail } from "lucide-react";
import { ASSETS, LINKS } from "../lib/constants";
import Footer from "../components/Footer";
import useBreadcrumbJsonLd from "../lib/useBreadcrumbJsonLd";

function useDocumentMeta({ title, description, path }) {
    useEffect(() => {
        // Reset scroll position on mount — SPA navigation otherwise
        // carries the previous page's scroll over, which is jarring
        // when arriving at a legal page from an inline /privacy-policy
        // link inside an FAQ answer halfway down the FAQ.
        window.scrollTo({ top: 0, left: 0, behavior: "instant" in window ? "instant" : "auto" });

        if (title) document.title = title;
        if (description) {
            let el = document.querySelector('meta[name="description"]');
            if (el) el.setAttribute("content", description);
        }
        if (path) {
            let canonical = document.querySelector('link[rel="canonical"]');
            if (canonical) canonical.setAttribute("href", `https://trtplus.app${path}`);
        }
    }, [title, description, path]);
}

export default function LegalLayout({ title, lastUpdated, children, testId, seoTitle, seoDescription, seoPath }) {
    useDocumentMeta({ title: seoTitle, description: seoDescription, path: seoPath });
    // Build a BreadcrumbList for this legal page so crawlers see
    // Home › <Page Title> in SERPs.
    useBreadcrumbJsonLd(
        seoPath && title
            ? [
                  { name: "Home", url: "https://trtplus.app/" },
                  { name: title, url: `https://trtplus.app${seoPath}` },
              ]
            : null
    );
    return (
        <main
            data-testid={testId}
            className="relative bg-black text-[#f4f5f7] font-body min-h-screen"
        >
            {/* slim header */}
            <header className="sticky top-0 z-40 bg-black/80 backdrop-blur-xl border-b border-white/5">
                <div className="max-w-4xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16">
                    <Link
                        to="/"
                        data-testid="legal-home-link"
                        className="flex items-center gap-2.5 text-white"
                    >
                        <img
                            src={ASSETS.icon}
                            alt=""
                            className="h-8 w-8 rounded-md"
                        />
                        <span className="font-brand text-2xl leading-none">
                            <span className="text-white">TRT</span>
                            <span className="text-[#2ba6e0]">+</span>
                        </span>
                    </Link>
                    <Link
                        to="/"
                        data-testid="legal-back-link"
                        className="inline-flex items-center gap-2 text-sm text-white/65 hover:text-white transition-colors"
                    >
                        <ArrowLeft size={16} /> Back to home
                    </Link>
                </div>
            </header>

            {/* hero */}
            <section className="relative pt-20 lg:pt-28 pb-12 lg:pb-16 overflow-hidden border-b border-white/5">
                <div className="absolute inset-0 grid-backdrop opacity-30 pointer-events-none" />
                <div className="absolute inset-0 radial-glow opacity-60 pointer-events-none" />
                <div className="relative max-w-4xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow mb-5">
                        <span className="dot" /> Legal
                    </div>
                    <h1
                        className="font-display font-bold text-white leading-tight"
                        style={{
                            fontSize: "clamp(2.25rem, 5vw, 3.75rem)",
                            letterSpacing: "-0.02em",
                        }}
                        data-testid="legal-title"
                    >
                        {title}
                    </h1>
                    {lastUpdated && (
                        <p className="mt-4 text-sm font-mono text-white/45 uppercase tracking-widest">
                            Last updated · {lastUpdated}
                        </p>
                    )}
                </div>
            </section>

            {/* body */}
            <article className="relative py-16 lg:py-20">
                <div className="max-w-3xl mx-auto px-6 lg:px-10 legal-prose">
                    {children}

                    <div className="hairline my-12" />
                    <div
                        className="tile p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between"
                        data-testid="legal-contact-card"
                    >
                        <div>
                            <div className="text-[11px] uppercase tracking-widest font-mono text-[#49bef5] mb-1">
                                Still have questions?
                            </div>
                            <div className="text-white/80 text-[15px]">
                                Every email gets a personal reply from the
                                developer.
                            </div>
                        </div>
                        <a
                            href={LINKS.email}
                            className="btn-blue inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm"
                        >
                            <Mail size={16} /> support@trtplus.app
                        </a>
                    </div>
                </div>
            </article>

            <Footer />
        </main>
    );
}
